/**
 * 通用工具方法模块
 * 提供链接打开、滚动、菜单切换等常用功能
 * 
 * @module common/utils/helpers
 * @author wei<innereyecw@gmail.com>
 * @version 5.0.0
 */
(function (global) {
  "use strict";

  // 确保命名空间存在
  if (!global.watashi) {
    global.watashi = {};
  }
  if (!global.watashi.common) {
    global.watashi.common = {};
  }

  const common = global.watashi.common;
  const logger = (global.watashi.core && global.watashi.core.logger) || console;

  /**
   * 平滑滚动到页面顶部
   * @param {Object} [options] - 配置选项
   * @param {number} [options.top=0] - 目标滚动位置
   * @param {string} [options.behavior='smooth'] - 滚动行为
   * @param {number} [options.duration=500] - 滚动动画持续时间（毫秒）
   * @param {Function} [options.onScrollEnd] - 滚动结束回调
   * 
   * @example
   * watashi.common.scrollToTop();
   * watashi.common.scrollToTop({ top: 100, behavior: 'smooth' });
   */
  common.scrollToTop = function (options) {
    const defaults = {
      top: 0,
      behavior: 'smooth',
      duration: 500,
      onScrollEnd: function () {}
    };
    const settings = {...defaults, ...options};
    window.scrollTo(settings);
  };

  /**
   * 打开链接
   * @param {string} root - 基础URL
   * @param {string} uri - 附加到基础URL的URI
   * @param {string} [target='_blank'] - 打开链接的目标窗口
   * @returns {boolean} 是否成功打开
   * 
   * @example
   * watashi.common.openLink('https://example.com/', 'page.html');
   * watashi.common.openLink('https://example.com/', 'page.html', '_self');
   */
  common.openLink = function (root, uri, target = '_blank') {
    if (typeof root !== 'string' || root.trim() === '') {
      return false;
    }

    if (uri === 'unknow' || typeof uri === "undefined") {
      return false;
    }

    const windowTarget = typeof target !== "undefined" ? target : '_blank';
    const encodedUri = encodeURIComponent(uri);

    try {
      if (root.trim() !== '' && uri.trim() !== '') {
        const fullUrl = root + encodedUri;
        window.open(fullUrl, windowTarget);
      } else {
        window.open(root + uri, windowTarget);
      }
    } catch (error) {
      logger.error(`Open link failed: ${error}`);
      return false;
    }

    return true;
  };

  /**
   * 切换菜单显示状态
   * @param {Object} params - 配置参数
   * @param {string} params.nameSelector - 菜单名称选择器
   * @param {string} params.containerSelector - 菜单容器选择器
   * @param {string} [params.hoverClass='hover'] - 悬停样式类名
   * @param {Event} event - 点击事件对象
   * 
   * @example
   * element.addEventListener('click', function(e) {
   *   watashi.common.toggleMenu({
   *     nameSelector: '.menu-item',
   *     containerSelector: '.submenu',
   *     hoverClass: 'active'
   *   }, e);
   * });
   */
  common.toggleMenu = function (params, event) {
    const th = event.target;
    const menuNames = document.querySelectorAll(params.nameSelector);
    const menuSubContainers = document.querySelectorAll(params.containerSelector);
    const currentSub = th.nextElementSibling;
    const hoverClass = params.hoverClass || 'hover';

    if (currentSub) {
      // 移除其他菜单的悬停状态
      menuNames.forEach(menu => {
        if (menu === th) return;
        menu.classList.remove(hoverClass);
      });
      th.classList.toggle(hoverClass);

      // 移除其他子容器的悬停状态
      menuSubContainers.forEach(container => {
        if (container === currentSub) return;
        container.classList.remove(hoverClass);
      });
      currentSub.classList.toggle(hoverClass);
    }
  };

  /**
   * 设置回到顶部按钮
   * @param {Object} [options] - 配置选项
   * @param {string} [options.selector='#backTop'] - 按钮元素选择器
   * @param {number} [options.threshold=400] - 显示按钮的滚动阈值（px）
   * @param {number} [options.fadeDuration=500] - 淡入淡出动画时长（ms）
   * 
   * @example
   * watashi.common.setupBackToTopButton();
   * watashi.common.setupBackToTopButton({ selector: '#myBackBtn', threshold: 300 });
   */
  common.setupBackToTopButton = function (options) {
    const defaults = {
      selector: '#backTop',
      threshold: 400,
      fadeDuration: 500,
    };
    const settings = {...defaults, ...options};
    const element = document.querySelector(settings.selector);
    if (!element) return;

    // 设置初始状态为不可见
    element.style.display = 'block';
    element.style.opacity = 0;
    element.style.transition = `opacity ${settings.fadeDuration}ms`;

    function toggleVisibility() {
      const distanceToTop = document.documentElement.scrollTop || window.pageYOffset || document.body.scrollTop;
      if (distanceToTop > settings.threshold) {
        element.style.opacity = 1;
      } else {
        element.style.opacity = 0;
      }
    }

    // 初始化事件监听器
    window.addEventListener('scroll', toggleVisibility);

    // 页面加载完成后立即检查一次滚动位置
    toggleVisibility();
  };

  /**
   * 初始化价格格式化（千分位 + 日元标记）
   * @param {Object} [options] - 配置选项
   * @param {string} [options.selector='.price'] - 价格元素选择器
   * 
   * @example
   * watashi.common.initializePriceFormatting();
   * watashi.common.initializePriceFormatting({ selector: '.product-price' });
   */
  common.initializePriceFormatting = function (options) {
    const defaults = {
      selector: '.price',
    };
    const settings = {...defaults, ...options};

    function formatNumber(elem) {
      const number = Number(elem.textContent);
      if (!isNaN(number) && elem.textContent.trim() !== '') {
        elem.innerHTML = `${common.thousandsSeparatorFormat(number)} <i>日元（税前）</i>`;
      } else {
        elem.innerHTML = '**** <i>日元（税前）</i>';
      }
    }

    const elements = document.querySelectorAll(settings.selector);
    elements.forEach(formatNumber);
  };

  /**
   * 操作元素的 className
   * @param {Object} options - 配置选项
   * @param {string} options.selector - 元素选择器
   * @param {string} options.className - 要操作的类名
   * @param {string} [options.action='add'] - 操作类型：'add' | 'remove' | 'toggle'
   * 
   * @example
   * watashi.common.toggleElementClass({ selector: '#myEl', className: 'active', action: 'add' });
   * watashi.common.toggleElementClass({ selector: '#myEl', className: 'active', action: 'toggle' });
   */
  common.toggleElementClass = function (options) {
    const defaults = {
      selector: '',
      className: '',
      action: 'add',
    };
    const settings = {...defaults, ...options};
    const elem = document.querySelector(settings.selector);

    if (!elem) {
      return;
    }

    switch (settings.action) {
      case 'add':
        elem.classList.add(settings.className);
        break;
      case 'remove':
        elem.classList.remove(settings.className);
        break;
      case 'toggle':
        elem.classList.toggle(settings.className);
        break;
      default:
        break;
    }
  };

  /**
   * 包裹子元素（将父元素的所有子元素移动到一个新的包装器中）
   * @param {Object} options - 配置选项
   * @param {Element} options.parentEl - 父元素
   * @param {string} [options.parentStyle] - 父元素样式
   * @param {string} options.wrapperTag - 包装器标签名
   * @param {string} [options.wrapperId] - 包装器ID
   * @param {string} [options.wrapperClass] - 包装器类名
   * @param {string} [options.wrapperStyle] - 包装器样式
   * 
   * @example
   * watashi.common.wrapChildElement({
   *   parentEl: document.body,
   *   wrapperTag: 'div',
   *   wrapperId: 'wrapper',
   *   wrapperClass: 'container'
   * });
   */
  common.wrapChildElement = function (options) {
    const defaults = {
      parentEl: '',
      parentStyle: '',
      wrapperTag: '',
      wrapperId: '',
      wrapperClass: '',
      wrapperStyle: '',
    };
    const settings = {...defaults, ...options};

    if (typeof settings.wrapperTag !== 'string') {
      return;
    }

    if (!(settings.parentEl instanceof Element)) {
      return;
    }

    const wrapper = document.createElement(settings.wrapperTag);
    if (settings.wrapperId) {
      wrapper.id = settings.wrapperId;
    }
    if (settings.wrapperClass) {
      wrapper.classList.add(settings.wrapperClass);
    }
    if (settings.wrapperStyle) {
      wrapper.style.cssText = settings.wrapperStyle;
    }

    settings.parentEl.appendChild(wrapper);
    while (settings.parentEl.firstChild && settings.parentEl.firstChild !== wrapper) {
      wrapper.appendChild(settings.parentEl.firstChild);
    }

    if (settings.parentStyle) {
      settings.parentEl.style.cssText = settings.parentStyle;
    }
  };

})(typeof window !== "undefined" ? window : this);
